import { Sparkbars } from '@/components/charts/Sparkbars';
import { projectAccent } from '@/lib/deterministic-color';
import { cn } from '@/lib/utils';

const fmtHours = (minutes: number): string => {
  if (!minutes || minutes <= 0) return '0h';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const fmtMoney = (cents: number | undefined | null, currency: string | undefined): string => {
  if (cents === undefined || cents === null) return '—';
  if (!currency) return (cents / 100).toFixed(2);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  } catch {
    return (cents / 100).toFixed(2);
  }
};

export type BillingType = 'none' | 'flat' | 'hourly';

const BILLING_LABEL: Record<BillingType, string> = {
  hourly: 'Hourly',
  flat: 'Flat fee',
  none: 'Non-billable',
};

const BILLING_STYLE: Record<BillingType, string> = {
  hourly:
    'bg-[oklch(0.85_0.13_245/0.2)] text-[oklch(0.45_0.13_245)] dark:text-[oklch(0.82_0.13_245)]',
  flat:
    'bg-[oklch(0.74_0.16_50/0.2)] text-[oklch(0.45_0.16_50)] dark:text-[oklch(0.85_0.16_50)]',
  none:
    'bg-[var(--surface-quiet,var(--secondary))] text-foreground-faint',
};

export interface ReportsProjectRowProps {
  projectSlug: string;
  name: string;
  billingType: BillingType;
  /** Daily minutes for this project across the visible range. Empty
   *  array hides the sparkbar column visually (a placeholder div
   *  keeps the grid aligned). */
  dailyMinutes: ReadonlyArray<number>;
  weekendIndices?: ReadonlyArray<number> | null;
  totalMinutes: number;
  /** Time-based amount only (excludes flat fee). */
  timeAmountCents: number;
  /** Total = time + flat fee (when applicable). */
  totalAmountCents: number;
  currency: string | undefined;
}

/**
 * Reports redesign — by-project row. CSS-grid layout matching the
 * prototype's `.v1-rep-proj` template:
 *   `14px  minmax(180px,1.4fr)  110px  1fr  90px  90px  110px`
 *     rail   name                   billing  spark hours  time   total
 *
 * The rail color is derived deterministically from the project
 * slug via `projectAccent` (same helper as Notes).
 */
export const ReportsProjectRow = ({
  projectSlug,
  name,
  billingType,
  dailyMinutes,
  weekendIndices,
  totalMinutes,
  timeAmountCents,
  totalAmountCents,
  currency,
}: ReportsProjectRowProps) => {
  const railColor = projectAccent(projectSlug);
  const hasSpark = dailyMinutes.length > 0;
  const hasTime = timeAmountCents > 0 && billingType === 'hourly';

  return (
    <div
      className={cn(
        'grid items-center gap-x-4 border-b px-[18px] py-3.5 transition-colors duration-100',
        'grid-cols-[14px_minmax(180px,1.4fr)_110px_1fr_90px_90px_110px]',
        'border-[var(--glass-card-border)] last:border-b-0',
        'hover:bg-[oklch(0_0_0/0.025)] dark:hover:bg-[oklch(1_0_0/0.04)]',
      )}
    >
      <span
        aria-hidden="true"
        className="h-8 w-[3px] rounded-full opacity-70"
        style={{ background: railColor }}
      />

      <div className="flex min-w-0 flex-col">
        <span
          className={cn(
            'text-foreground truncate text-[13.5px] font-medium tracking-[-0.005em]',
          )}
        >
          {name}
        </span>
        <span
          className={cn(
            'text-foreground-faint mt-0.5 truncate font-mono text-[11px]',
          )}
        >
          /{projectSlug}
        </span>
      </div>

      <span
        className={cn(
          'inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-[3px] text-[10.5px] font-medium',
          BILLING_STYLE[billingType],
        )}
      >
        <span
          aria-hidden="true"
          className="size-[5px] rounded-full bg-current"
        />
        {BILLING_LABEL[billingType]}
      </span>

      {hasSpark ? (
        <Sparkbars
          days={dailyMinutes}
          color={`${projectAccent(projectSlug).slice(0, -1)} / 0.7)`}
          weekendIndices={weekendIndices}
        />
      ) : (
        <div />
      )}

      <span
        className={cn(
          'text-right font-mono text-[12px] tabular-nums',
          totalMinutes === 0 ? 'text-foreground-faint opacity-70' : 'text-foreground-muted',
        )}
      >
        {fmtHours(totalMinutes)}
      </span>

      <span
        className={cn(
          'text-right font-mono text-[12px] tabular-nums',
          hasTime ? 'text-foreground-muted' : 'text-foreground-faint opacity-70',
        )}
      >
        {hasTime ? fmtMoney(timeAmountCents, currency) : '—'}
      </span>

      <span
        className={cn(
          'text-foreground text-right font-mono text-[13px] font-semibold tracking-[-0.01em] tabular-nums',
        )}
      >
        {totalAmountCents > 0 ? fmtMoney(totalAmountCents, currency) : '—'}
      </span>
    </div>
  );
};

/**
 * The header row that sits above the project rows. Uses the same
 * grid template so columns align under it.
 */
export const ReportsProjectHeader = ({ showSpark }: { showSpark: boolean }) => (
  <div
    className={cn(
      'grid gap-x-4 border-b px-[18px] py-2.5 font-mono text-[9.5px] font-semibold uppercase tracking-[0.08em]',
      'grid-cols-[14px_minmax(180px,1.4fr)_110px_1fr_90px_90px_110px]',
      'text-foreground-faint border-[var(--glass-card-border)]',
      'bg-[oklch(0_0_0/0.015)] dark:bg-[oklch(1_0_0/0.02)]',
    )}
  >
    <span />
    <span>Project</span>
    <span>Type</span>
    <span>{showSpark ? 'Daily activity' : ''}</span>
    <span className="text-right">Hours</span>
    <span className="text-right">Time</span>
    <span className="text-right">Total</span>
  </div>
);
